import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { ListFilters } from "../../../../../interfaces/property.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  status: any[];
  allStatus: any[];
  managers: any[];
  allManagers: any[];
  customers: any[];
  allCustomers: any[];
  cities: any[];
  allCities: any[];
  states: any[];
  allStates: any[];
  linkedWithHospitable: any[];
  allLinkedWithHospitable: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  status, allStatus, managers, allManagers, customers, allCustomers, cities, allCities,
  states, allStates, linkedWithHospitable, allLinkedWithHospitable, dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  // Calculate initial filtersApplied state
  const isApplied = filters.searchText !== '' ||
    filters.customer !== '' ||
    filters.city !== '' ||
    filters.state !== '' ||
    filters.assignedTo !== '' ||
    filters.linkedWithHospitable !== '' ||
    filters.status !== '1';

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText,
      myProperties: initialFilters.myProperties // Explicitly preserve myProperties as per original logic
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.customer !== '' ||
      filtersToApply.city !== '' ||
      filtersToApply.state !== '' ||
      filtersToApply.assignedTo !== '' ||
      filtersToApply.linkedWithHospitable !== '' ||
      filtersToApply.status !== '1';

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.customer, filters.city, filters.state, filters.assignedTo, filters.linkedWithHospitable, filters.status, initialFilters.myProperties, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.state) {
      setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value, city: '' }));
    } else {
      setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
    }
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      status: '1',
      assignedTo: '',
      customer: '',
      city: '',
      state: '',
      linkedWithHospitable: '',
      myProperties: initialFilters.myProperties, // Preserve myProperties toggle
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-2 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("property.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.customer ? [filters.customer] : []}
                options={(customers || []).map((customer: any) => ({
                  label: customer.name,
                  value: customer.id
                }))}
                allOptions={(allCustomers || []).map((customer: any) => ({
                  label: customer.name,
                  value: customer.id
                }))}
                id={VALIDATION_FIELD_MAPPING.customer}
                name={VALIDATION_FIELD_MAPPING.customer}
                searchText={dropdownSearchData.customer}
                onSearch={onDropdownSearchChange}
                placeholder={t("property.list.pageTexts.filters.selectCustomer")}
                label={t("property.list.pageTexts.filters.customer")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.state && filters.state.toString().trim() ? [filters.state] : []}
                options={(states || []).map((state: any) => ({
                  label: state.name,
                  value: state.id
                }))}
                allOptions={(allStates || []).map((state: any) => ({
                  label: state.name,
                  value: state.id
                }))}
                id={VALIDATION_FIELD_MAPPING.state}
                name={VALIDATION_FIELD_MAPPING.state}
                searchText={dropdownSearchData.state}
                onSearch={onDropdownSearchChange}
                placeholder={t("property.list.pageTexts.filters.selectProvince")}
                label={t("property.list.pageTexts.filters.province")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.city && filters.city.toString().trim() ? [filters.city] : []}
                options={(cities || []).map((city: any) => ({
                  label: city.name,
                  value: city.id
                }))}
                allOptions={(allCities || []).map((city: any) => ({
                  label: city.name,
                  value: city.id
                }))}
                id={VALIDATION_FIELD_MAPPING.city}
                name={VALIDATION_FIELD_MAPPING.city}
                searchText={dropdownSearchData.city}
                onSearch={onDropdownSearchChange}
                placeholder={t("property.list.pageTexts.filters.selectCity")}
                label={t("property.list.pageTexts.filters.city")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.assignedTo && filters.assignedTo.toString().trim() ? [filters.assignedTo] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                id={VALIDATION_FIELD_MAPPING.assignedTo}
                name={VALIDATION_FIELD_MAPPING.assignedTo}
                searchText={dropdownSearchData.assignedTo}
                onSearch={onDropdownSearchChange}
                placeholder={t("property.list.pageTexts.filters.selectAssignedTo")}
                label={t("property.list.pageTexts.filters.assignedTo")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.linkedWithHospitable && filters.linkedWithHospitable.toString().trim() ? [filters.linkedWithHospitable] : []}
                options={(linkedWithHospitable || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allLinkedWithHospitable || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.linkedWithHospitable}
                name={VALIDATION_FIELD_MAPPING.linkedWithHospitable}
                placeholder={t('property.list.pageTexts.filters.selectLinkedWithHospitable')}
                label={t('property.list.pageTexts.filters.linkedWithHospitable')}
                onChange={handleInputChange}
                searchText={dropdownSearchData.linkedWithHospitable}
                onSearch={onDropdownSearchChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.status && filters.status.toString().trim() ? [filters.status] : []}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("property.list.pageTexts.filters.selectStatus")}
                label={t("property.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;